import React, {
  useCallback, useContext, useEffect, useReducer, useState,
} from 'react';
import {
  NavLink, Redirect, Route, useLocation, useParams,
} from 'react-router-dom';
import { ModuleNav, Title } from 'shared/components/abstract/interactables';
import { buildingEditCheck, getBuilding } from 'shared/api/buildings';
import { checkPermissions } from 'shared/api/auth';
import { ArchivedBadge } from 'shared/components/ArchiveSwitch';
import { SideNavContext } from 'shared/hoc/withSideNav';
import { useRequest } from 'shared/hooks';
import { networkingViewCheck } from 'shared/api/networking';
import { searchTickets } from 'shared/api/support';
import { ActiveStyledBadge } from 'shared/components/styled/graphics';
import { CardTab } from 'shared/components/styled/tabs';
import ArchiveCard from 'shared/components/styled/cards';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import { getBuildingPath, viewPath } from 'shared/routes/building';

const initialState = {
  editEnabled: false,
  ...initialDataReducerState,
};

function buildingReducer(state, { action, payload }) {
  switch (action) {
    case 'set': {
      if (!payload) {
        return { ...initialState, data: null };
      }

      return {
        ...state,
        data: payload,
        updatedData: {},
        dirty: false,
      };
    }
    case 'state':
      return { ...state, ...payload };
    default:
      return dataReducer(state, { action, payload });
  }
}

function getDeepestRouteMatch(pathname, possiblePaths) {
  const possibleMatches = [];
  for (let i = 0; i < possiblePaths.length; i += 1) {
    const possiblePath = possiblePaths[i];
    let currentPath = pathname;
    // if one is longer than the other, get the substring and compare
    if (currentPath.length > possiblePath.length) {
      currentPath = pathname.substring(0, possiblePath.length);
    }
    if (possiblePath === currentPath) {
      possibleMatches.push(currentPath);
    }
  }

  // the longest possible match will be most like the nested path at the current location
  if (possibleMatches.length > 0) {
    return possibleMatches.reduce(
      (a, b) => (a.length > b.length ? a : b),
    );
  }

  return pathname;
}

/**
 * @param {Array<Object>} enabledRoutes - Array of route objects with text and getPath properties
 */
export default function BuildingNav({ enabledRoutes }) {
  const { request } = useRequest();
  const [subtitle, setSubtitle] = useState(undefined);
  const [buildingState, buildingDispatch] = useReducer(buildingReducer, initialState);
  const syncSideNav = useContext(SideNavContext);
  const { data: building } = buildingState;
  const [updateCount, setUpdateCount] = useState(0);
  const reloadBuilding = useCallback(() => setUpdateCount((c) => c + 1), []);
  const { routeId } = useParams();
  const location = useLocation();
  const possiblePaths = enabledRoutes.map((route) => route.getPath(routeId));

  useEffect(() => {
    (async () => {
      const response = await request(checkPermissions([
        buildingEditCheck,
        networkingViewCheck,
      ]));
      if (response) {
        buildingDispatch({
          action: 'state',
          payload: {
            editEnabled: response[0],
            canAccessNetworking: response[1],
          },
        });
      }
    })();
  }, [request]);

  useEffect(() => {
    (async () => {
      const buildingResponse = await request(getBuilding(routeId, '?expand=units&expand=nas'));
      if (buildingResponse !== undefined && buildingResponse !== null) {
        const ticketResponse = await request(searchTickets(`?building_id=${routeId}&unit_id=0&tenant_id=0&status=Active`));
        if ((ticketResponse !== undefined && ticketResponse !== null)
          && ticketResponse.rows.length > 0) {
          buildingResponse.tickets = ticketResponse.rows;
        }
        buildingDispatch({ action: DataReducerActions.set, payload: buildingResponse });
      }
    })();
  }, [routeId, request, updateCount]);

  useEffect(() => {
    if (building) {
      syncSideNav(getBuildingPath(building.id), building.name);
    }
  }, [building, syncSideNav]);

  if (building === undefined) return null;
  if (building === null) return <Redirect to={viewPath} />;

  const routes = [];
  const tabLinks = [];

  enabledRoutes.forEach(({
    text,
    getPath,
    RouteComponent,
    exact,
    dependsOn,
    filters = '',
    key = '',
  }) => {
    const path = getPath(routeId);

    // Skip building components that don't have the necessary references.
    // Example: security needs building.building_security
    if (dependsOn && !building[dependsOn]) return;

    const tabLink = (
      <CardTab value={path} key={path} label={text} component={NavLink} pathname={path} to={path} />
    );

    tabLinks.push(text === 'Tickets'
      ? (
        <CardTab
          key={path}
          label={(
            <ActiveStyledBadge overlap="circular" key={path} badgeContent={building?.tickets?.length}>
              {text}
            </ActiveStyledBadge>
          )}
          value={path}
          component={NavLink}
          pathname={path}
          to={path}
          id={`simple-tabpanel-${text}`}
          aria-controls={`simple-tabpanel-${text}`}
        />
      )
      : tabLink);

    routes.push((
      <Route
        key={path}
        path={path}
        exact={exact}
        render={() => (
          <RouteComponent
            buildingDispatch={buildingDispatch}
            buildingState={buildingState}
            reloadBuilding={reloadBuilding}
            setSubtitle={setSubtitle}
            filterKey={key}
            savedFilters={filters}
            path={path}
          />
        )}
      />
    ));
  });

  return (
    <>
      <Title title={building.name} subtitle={subtitle} titleLink={getBuildingPath(building.id)}>
        <ArchivedBadge archivedAt={building.archived_at} />
      </Title>
      <ArchiveCard archived={building.archived_at}>
        <ModuleNav links={tabLinks} value={getDeepestRouteMatch(location.pathname, possiblePaths)} label="BuildingNav" />
        {routes}
      </ArchiveCard>
    </>
  );
}
